import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useNarrativeState, usePlanningState, useUpdatePlanningField } from '../store/hooks';

import { ConceptPreview } from './ConceptPreview';

interface BPlotModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  skipIntro?: boolean;
}

const REASON_OPTIONS = [
  { value: 'intimidate', label: 'Intimidate' },
  { value: 'intrigue', label: 'Intrigue' },
  { value: 'evade', label: 'Evade' },
  { value: 'show_value', label: 'Show Value' },
  { value: 'be_understood', label: 'Be Understood' },
  { value: 'find_common_ground', label: 'Find Common Ground' },
  { value: 'other', label: '(Enter a custom option)' },
];

const NATURE_OPTIONS = [
  { value: 'anecdote', label: 'An Anecdote' },
  { value: 'philosophical_point', label: 'A Philosophical Point' },
  { value: 'parable', label: 'A Parable' },
  { value: 'memory', label: 'A Memory' },
  { value: 'personal_revelation', label: 'A Personal Revelation' },
  { value: 'option', label: 'An Option' },
  { value: 'other', label: '(Enter a custom option)' },
];

export const BPlotModal = React.memo(function BPlotModal({
  isOpen,
  onClose,
  onComplete,
  skipIntro = false,
}: BPlotModalProps) {
  const planningState = usePlanningState();
  const narrativeState = useNarrativeState();
  const updatePlanningField = useUpdatePlanningField();
  const [showIntro, setShowIntro] = useState(!skipIntro);

  // Reset showIntro state when modal opens based on skipIntro prop
  useEffect(() => {
    if (isOpen) {
      setShowIntro(!skipIntro);
    }
  }, [isOpen, skipIntro]);

  // Get Segment 1 Node 2 data
  const node2Data = narrativeState.nodes['S1_Player_Declaration'];

  const canContinue = useMemo(
    () =>
      planningState.bPlotReason &&
      planningState.bPlotNature &&
      planningState.bPlotExplanation.trim() &&
      (planningState.bPlotReason !== 'other' || planningState.bPlotReasonOther.trim()) &&
      (planningState.bPlotNature !== 'other' || planningState.bPlotNatureOther.trim()),
    [planningState]
  );

  const handleContinue = useCallback(() => {
    if (canContinue) {
      onComplete();
      onClose();
    }
  }, [canContinue, onComplete, onClose]);

  const handleReasonChange = useCallback(
    (value: string) => {
      updatePlanningField('bPlotReason', value as any);
      if (value !== 'other') {
        updatePlanningField('bPlotReasonOther', '');
      }
    },
    [updatePlanningField]
  );

  const handleNatureChange = useCallback(
    (value: string) => {
      updatePlanningField('bPlotNature', value as any);
      if (value !== 'other') {
        updatePlanningField('bPlotNatureOther', '');
      }
    },
    [updatePlanningField]
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-gradient-to-b from-[#050614] to-[#0f1a2f] border border-[#1a2d4d] rounded-xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col"
          >
            {/* Header */}
            {!showIntro && (
              <div className="bg-gradient-to-br from-[#0c1222] to-[#0a1020] border-b border-[#1a2d4d] px-8 py-6">
                <div>
                  <h2 className="text-3xl font-bold uppercase bg-gradient-to-r from-[#4f46e5] to-[#06b6d4] bg-clip-text text-transparent">
                    B-Plot & Character Nuance
                  </h2>
                  <p className="text-sm text-gray-400 mt-2">
                    {`Preparing for the core "Diversion and Return" mechanic`}
                  </p>
                </div>
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {showIntro ? (
                <div className="p-8 text-center">
                  <div className="max-w-2xl mx-auto space-y-6">
                    <h3 className="text-2xl font-bold text-white">
                      Great work on completing Segment 1!
                    </h3>
                    <div className="bg-[#0c1222] border border-[#1a2d4d] rounded-lg p-6 text-left space-y-4">
                      <h4 className="text-lg font-semibold text-white">The NPC B-Plot Begins:</h4>
                      <p className="text-gray-400 text-sm">
                        The B-Plot here will be a diversion or unexpected turn in the conversation
                        that:
                      </p>
                      <ul className="list-disc list-inside space-y-2 text-gray-400 text-sm ml-4">
                        <li>
                          <em>seems</em> to shift focus from the main conflict (A-Plot)
                        </li>
                        <li>
                          represents a strategy on the part of the NPC, whether conscious or
                          unconscious -- they want something
                        </li>
                        <li>
                          can be an <em>offer</em> (meant to bridge a connection and make an appeal)
                        </li>
                        <li>
                          can be a <em>provocation</em> (meant to intimidate or put the other person
                          in their place)
                        </li>
                        <li>can build tension through philosophical tangents or power plays</li>
                        <li>can provide breathing room before returning to the main conflict</li>
                      </ul>
                    </div>
                    <p className="text-gray-300">
                      The important thing to remember is that people do these things for a{' '}
                      <em>reason</em> but these reasons don&apos;t <em>have</em> to work out the way
                      they want them to work out.
                    </p>
                    <button
                      onClick={() => setShowIntro(false)}
                      className="px-8 py-3 bg-gradient-to-r from-[#4f46e5] to-[#06b6d4] text-white font-semibold rounded-lg hover:shadow-lg hover:scale-105 transition-all"
                    >
                      Start B-Plot Planning &rarr;
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-8 max-h-[75vh] overflow-y-auto">
                  <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-6">
                    {/* Questions Section */}
                    <div className="space-y-6">
                      {/* Questions 1 & 2 Side by Side */}
                      <div className="grid grid-cols-2 gap-4">
                        {/* Question 1: Primary Reason */}
                        <div className="bg-gradient-to-br from-[#0c1222] to-[#0a1020] border border-[#1a2d4d] rounded-xl p-6 shadow-lg">
                          <h3 className="text-base font-semibold text-gray-300 mb-3">Question 1</h3>
                          <p className="text-gray-400 text-xs mb-4">
                            What is the primary reason why your NPC chooses to shift things at this
                            point? Consider your NPC&apos;s underlying motivations.
                          </p>

                          <div className="space-y-2">
                            {REASON_OPTIONS.map((option) => (
                              <label
                                key={option.value}
                                className="flex items-center space-x-2 cursor-pointer text-xs"
                              >
                                <input
                                  type="radio"
                                  name="bPlotReason"
                                  value={option.value}
                                  checked={planningState.bPlotReason === option.value}
                                  onChange={(e) => handleReasonChange(e.target.value)}
                                  className="text-[#4f46e5] focus:ring-[#4f46e5] focus:ring-1"
                                />
                                <span className="text-gray-300">{option.label}</span>
                              </label>
                            ))}
                          </div>

                          {planningState.bPlotReason === 'other' && (
                            <div className="mt-3">
                              <input
                                type="text"
                                placeholder="Enter your alternative option..."
                                value={planningState.bPlotReasonOther}
                                onChange={(e) =>
                                  updatePlanningField('bPlotReasonOther', e.target.value)
                                }
                                className="w-full bg-[#0a0f1f] border border-[#1a2d4d] rounded-lg p-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#4f46e5] focus:border-transparent"
                              />
                            </div>
                          )}
                        </div>

                        {/* Question 2: Nature of Diversion */}
                        <div className="bg-gradient-to-br from-[#0c1222] to-[#0a1020] border border-[#1a2d4d] rounded-xl p-6 shadow-lg">
                          <h3 className="text-base font-semibold text-gray-300 mb-3">Question 2</h3>
                          <p className="text-gray-400 text-xs mb-4">
                            {`What is the nature of the NPC's conversational diversion?`}
                          </p>

                          <div className="space-y-2">
                            {NATURE_OPTIONS.map((option) => (
                              <label
                                key={option.value}
                                className="flex items-center space-x-2 cursor-pointer text-xs"
                              >
                                <input
                                  type="radio"
                                  name="bPlotNature"
                                  value={option.value}
                                  checked={planningState.bPlotNature === option.value}
                                  onChange={(e) => handleNatureChange(e.target.value)}
                                  className="text-[#4f46e5] focus:ring-[#4f46e5] focus:ring-1"
                                />
                                <span className="text-gray-300">{option.label}</span>
                              </label>
                            ))}
                          </div>

                          {planningState.bPlotNature === 'other' && (
                            <div className="mt-3">
                              <input
                                type="text"
                                placeholder="Enter your alternative option..."
                                value={planningState.bPlotNatureOther}
                                onChange={(e) =>
                                  updatePlanningField('bPlotNatureOther', e.target.value)
                                }
                                className="w-full bg-[#0a0f1f] border border-[#1a2d4d] rounded-lg p-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#4f46e5] focus:border-transparent"
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Question 3: Explanation */}
                      <div className="bg-gradient-to-br from-[#0c1222] to-[#0a1020] border border-[#1a2d4d] rounded-xl p-6 shadow-lg">
                        <h3 className="text-base font-semibold text-gray-300 mb-3">Question 3</h3>
                        <p className="text-gray-400 text-xs mb-4">
                          Why did your NPC choose this particular method to divert the conversation,
                          given their underlying motivations?
                        </p>

                        <textarea
                          value={planningState.bPlotExplanation}
                          onChange={(e) => updatePlanningField('bPlotExplanation', e.target.value)}
                          placeholder="Explain the connection between your NPC's reason for diverting and their chosen method..."
                          className="w-full h-32 bg-[#0a0f1f] border border-[#1a2d4d] rounded-lg p-3 text-sm text-white placeholder-gray-500 resize-none focus:outline-none focus:ring-2 focus:ring-[#4f46e5] focus:border-transparent"
                        />
                        <div className="text-right text-xs text-gray-500 mt-2">
                          {planningState.bPlotExplanation.length} characters
                        </div>
                      </div>
                    </div>

                    {/* Right Column: Previews */}
                    <div className="lg:sticky lg:top-4 lg:h-fit space-y-6">
                      {/* Scene Concept Preview */}
                      <div className="bg-gradient-to-br from-[#0c1222] to-[#0a1020] border border-[#1a2d4d] rounded-xl shadow-lg overflow-hidden">
                        <div className="bg-gradient-to-r from-[#4f46e5]/20 to-[#06b6d4]/20 px-6 py-3 border-b border-[#1a2d4d]">
                          <h4 className="text-sm font-semibold text-gray-300">
                            Scene Concept Preview
                          </h4>
                        </div>
                        <div className="p-6">
                          <ConceptPreview
                            planningState={planningState}
                            includeSegment2Summary={true}
                          />
                        </div>
                      </div>

                      {/* Node Preview - Compact */}
                      <div className="bg-[#0a0f1f] border border-[#1a2d4d]/50 rounded-lg overflow-hidden">
                        <div className="bg-[#0a0f1f] px-3 py-2 border-b border-[#1a2d4d]/50">
                          <h4 className="text-xs font-medium text-gray-400">
                            The previous node leading into the NPC&apos;s shift to this B-plot
                            featured:
                          </h4>
                        </div>

                        <div className="p-3">
                          {node2Data?.text && (
                            <div className="mb-2">
                              <div className="text-gray-300 leading-relaxed text-xs bg-[#050914] p-2 rounded border border-[#1a2d4d]/30">
                                {node2Data.text.length > 150
                                  ? `${node2Data.text.substring(0, 150)}...`
                                  : node2Data.text}
                              </div>
                            </div>
                          )}

                          {node2Data?.choices &&
                            node2Data.choices.some((choice) => choice.text.trim()) && (
                              <div className="space-y-1">
                                {node2Data.choices.map(
                                  (choice, index) =>
                                    choice.text.trim() && (
                                      <div
                                        key={choice.id}
                                        className="bg-[#1a2d2f] border border-[#059669]/30 text-gray-300 rounded px-2 py-1 text-xs"
                                      >
                                        <span className="text-gray-400">Choice {index + 1}:</span>{' '}
                                        {choice.text.length > 60
                                          ? `${choice.text.substring(0, 60)}...`
                                          : choice.text}
                                      </div>
                                    )
                                )}
                              </div>
                            )}

                          {!node2Data?.text &&
                            !node2Data?.choices?.some((choice) => choice.text.trim()) && (
                              <p className="text-xs text-gray-500 italic text-center py-4">
                                No content yet in Node 2
                              </p>
                            )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            {!showIntro && (
              <div className="bg-gradient-to-br from-[#0c1222] to-[#0a1020] border-t border-[#1a2d4d] px-8 py-6 flex justify-between items-center">
                <button
                  onClick={() => setShowIntro(true)}
                  className="px-6 py-3 text-sm text-gray-400 hover:text-white transition-colors font-semibold"
                >
                  &larr; Back to Introduction
                </button>

                <button
                  onClick={handleContinue}
                  disabled={!canContinue}
                  className={`px-8 py-3 rounded-lg font-semibold text-sm transition-all ${
                    canContinue
                      ? 'bg-gradient-to-r from-[#4f46e5] to-[#06b6d4] text-white hover:shadow-lg hover:scale-105'
                      : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Continue to Segment 2 &rarr;
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
});
